import React from 'react';
import { Settings, Calendar, Clock } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

const SettingsSidebar = ({ 
  formData,
  setFormData,
  errors,
  isGenerating
}) => {
  // Функция для обработки изменений в настройках
  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Предопределенные типы расписаний
  const scheduleTypes = [
    { id: 'weekly', name: 'Недельное', description: 'На неделю' },
    { id: 'monthly', name: 'Месячное', description: 'На месяц' },
    { id: 'semester', name: 'Семестровое', description: 'На семестр' },
    { id: 'custom', name: 'Произвольное', description: 'Своё' }
  ];
  
  // Предопределенные шаблоны настроек для разных учебных заведений
  const presetTemplates = [
    { 
      id: 'school', 
      name: 'Школа', 
      icon: 'school',
      settings: {
        eventDuration: 45,
        breakDuration: 10,
        dayStartTime: '08:30',
        dayEndTime: '15:30',
        workDays: ['1', '2', '3', '4', '5'],
        lunchBreak: true,
        lunchStartTime: '12:00',
        lunchDuration: 30
      }
    },
    { 
      id: 'university', 
      name: 'ВУЗ', 
      icon: 'university',
      settings: {
        eventDuration: 90,
        breakDuration: 10,
        dayStartTime: '09:00',
        dayEndTime: '18:00',
        workDays: ['1', '2', '3', '4', '5', '6'],
        lunchBreak: true,
        lunchStartTime: '13:00',
        lunchDuration: 60
      }
    },
    { 
      id: 'courses', 
      name: 'Курсы', 
      icon: 'courses',
      settings: {
        eventDuration: 60,
        breakDuration: 10,
        dayStartTime: '10:00',
        dayEndTime: '19:00',
        workDays: ['1', '2', '3', '4', '5', '6'],
        lunchBreak: false
      }
    },
    { 
      id: 'evening', 
      name: 'Вечерние занятия', 
      icon: 'evening',
      settings: {
        eventDuration: 90,
        breakDuration: 10,
        dayStartTime: '18:00',
        dayEndTime: '21:30',
        workDays: ['1', '2', '3', '4', '5'],
        lunchBreak: false
      }
    }
  ];
  
  // Функция для применения шаблона настроек
  const applyTemplate = (template) => {
    const settings = template.settings;
    
    // Применяем все настройки из шаблона к текущей форме
    Object.keys(settings).forEach(key => {
      handleInputChange(key, settings[key]);
    });
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрые настройки
      </h3>
      
      <div className="space-y-5">
        {/* Тип расписания */}
        <div>
          <label htmlFor="quickScheduleType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип расписания
          </label>
          <div className="grid grid-cols-2 gap-2">
            {scheduleTypes.map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handleInputChange('scheduleType', type.id)}
                className={`p-2 text-sm rounded-lg border transition-colors text-left ${
                  formData.scheduleType === type.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <div className="font-medium">{type.name}</div>
                <div className="text-xs opacity-75">{type.description}</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Период расписания */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Период расписания
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="quickStartDate" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Начало
              </label>
              <input 
                type="date"
                id="quickStartDate"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className={`form-input w-full ${errors.startDate ? 'input-error' : ''}`}
              />
            </div>
            <div>
              <label htmlFor="quickEndDate" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Окончание
              </label>
              <input 
                type="date"
                id="quickEndDate"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                className={`form-input w-full ${errors.endDate ? 'input-error' : ''}`}
              />
            </div>
          </div>
        </div>
        
        {/* Временные настройки */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Время занятий
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="quickDayStartTime" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Начало дня
              </label>
              <input 
                type="time"
                id="quickDayStartTime"
                value={formData.dayStartTime}
                onChange={(e) => handleInputChange('dayStartTime', e.target.value)}
                className={`form-input w-full ${errors.dayStartTime ? 'input-error' : ''}`}
              />
            </div>
            <div>
              <label htmlFor="quickDayEndTime" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Окончание дня
              </label>
              <input 
                type="time"
                id="quickDayEndTime"
                value={formData.dayEndTime}
                onChange={(e) => handleInputChange('dayEndTime', e.target.value)}
                className={`form-input w-full ${errors.dayEndTime ? 'input-error' : ''}`}
              />
            </div>
          </div>
        </div>
        
        {/* Длительность занятий */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Длительность (минуты)
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="quickEventDuration" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Занятие
              </label>
              <input 
                type="number"
                id="quickEventDuration"
                min="15"
                max="180"
                value={formData.eventDuration}
                onChange={(e) => handleInputChange('eventDuration', e.target.value)}
                className={`form-input w-full ${errors.eventDuration ? 'input-error' : ''}`}
              />
            </div>
            <div>
              <label htmlFor="quickBreakDuration" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Перерыв
              </label>
              <input 
                type="number"
                id="quickBreakDuration"
                min="5"
                max="30"
                value={formData.breakDuration}
                onChange={(e) => handleInputChange('breakDuration', e.target.value)}
                className={`form-input w-full ${errors.breakDuration ? 'input-error' : ''}`}
              />
            </div>
          </div>
        </div>
        
        {/* Шаблоны настроек */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
            <Calendar className="w-4 h-4 mr-1 text-indigo-500" />
            Готовые наборы настроек
          </h4>
          
          <div className="grid grid-cols-2 gap-2">
            {presetTemplates.map(template => (
              <button
                key={template.id}
                type="button"
                onClick={() => applyTemplate(template)}
                className="p-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="font-medium text-gray-800 dark:text-gray-200">{template.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {template.settings.eventDuration} мин, {template.settings.workDays.length} дн/нед
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка генерации */}
        <div className="pt-2">
          <Button
            primary
            fullWidth
            icon={<Calendar className="w-4 h-4" />}
            disabled={isGenerating || Object.keys(errors).length > 0}
            isLoading={isGenerating}
            onClick={() => {
              // Находим кнопку отправки формы и кликаем по ней
              document.querySelector('button[type="submit"]')?.click();
            }}
          >
            {isGenerating ? 'Генерация...' : 'Создать расписание'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsSidebar;